/**
 * The finish decisions of a review and what each one tells the owner: the
 * question before (`finishQuestion`) and the outcome after (`finishOutcome`).
 * The review detail page and the email-draft inbox rows share them, so a
 * button says the same thing wherever it sits.
 *
 * - Approve: the author works the comments in and closes – no further round.
 *   A document closes; an email becomes the mailbox draft.
 * - Request changes: the author works the comments in and resubmits.
 * - Email drafts approve one of two ways: "draft" (the seat creates the
 *   mailbox draft, the owner sends it) or "send" (the server sends exactly
 *   this content from the owner's mailbox – so never with comments).
 * - A briefing is never approved: "Send comments" is its request for
 *   changes; the meeting ends it (`lib/briefings.ts :: closeQuestion`).
 */
import type { AdminReviewDoc, EmailSignature } from "../api/admin";
import type { ConfirmOptions } from "../components/confirm/context";

export type FinishDecision = "approve" | "request_changes" | "reject";
export type EmailDelivery = "draft" | "send";

export interface FinishContext {
  /** The author's slug; absent when unknown. */
  author?: string | null;
  isEmail: boolean;
  /** A meeting briefing – its request for changes reads "Send comments". */
  isBriefing?: boolean;
  /** Draft comments plus the general feedback, if any. */
  feedbackCount: number;
  mailbox?: string | null;
  /** The files that go out with the email (its envelope's `attachments`). */
  attachments?: string[];
  /** Named in the question when the page does not show the email itself. */
  subject?: string | null;
  /** The signature the draft or send appends; absent where the page does
   * not know it (the inbox rows). */
  signature?: EmailSignature | null;
}

/** Why "Approve & send" waits: it sends exactly this content, a comment asks
 * for a change. The server refuses the same (`finish_delivery_error`). */
export const SEND_NEEDS_NO_COMMENTS =
  "Approve & send sends exactly this content – your comments are worked in with Approve & draft or Request changes";

const comments = (n: number) => `${n} comment${n === 1 ? "" : "s"}`;

/** " with 2 attachments (a.pdf, b.pdf)" – or nothing without files. */
function attached(names: string[] | undefined): string {
  if (!names?.length) return "";
  return ` with ${names.length === 1 ? "1 attachment" : `${names.length} attachments`} (${names.join(", ")})`;
}

/** " Your signature (…) is added." – or that none is, when the page knows. */
function signatureNote(signature: EmailSignature | null | undefined): string {
  if (!signature?.mailbox) return "";
  return signature.html
    ? ` Your signature for ${signature.mailbox} is added.`
    : ` No signature is on file for ${signature.mailbox} – it goes out without one.`;
}
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const authorOf = (ctx: FinishContext) => ctx.author ?? "the employee";

export function finishQuestion(
  decision: FinishDecision,
  delivery: EmailDelivery | undefined,
  ctx: FinishContext,
): ConfirmOptions {
  const author = authorOf(ctx);
  const n = ctx.feedbackCount;
  const what = ctx.subject
    ? `"${ctx.subject}"`
    : ctx.isEmail ? "this email" : ctx.isBriefing ? "this briefing" : "this document";

  if (decision === "reject") {
    return {
      title: "Reject this email permanently?",
      body: "No new version or mailbox draft will be created. Your reason is passed to the agent for future outreach.",
      confirmLabel: "Reject email",
      tone: "danger",
    };
  }
  if (decision === "request_changes" && ctx.isBriefing) {
    return {
      title: `Send your ${comments(n)} on ${what}?`,
      body: `${capitalize(author)} works them into the briefing and resubmits it before the meeting.`,
      confirmLabel: "Send comments",
    };
  }
  if (decision === "request_changes") {
    return {
      title: `Request changes to ${what}?`,
      body: ctx.isEmail
        ? `${capitalize(author)} revises the email with your ${comments(n)} and resubmits it for approval.`
        : `${capitalize(author)} works your ${comments(n)} in and resubmits the document for another review.`,
      confirmLabel: "Request changes",
    };
  }
  if (!ctx.isEmail) {
    return n
      ? {
          title: `Approve with ${comments(n)} as notes?`,
          body: `${capitalize(author)} applies them and closes the review. There is no further round.`,
          confirmLabel: "Approve",
        }
      : {
          title: `Approve ${what}?`,
          body: `${capitalize(author)} closes the review.`,
          confirmLabel: "Approve",
        };
  }
  const signed = signatureNote(ctx.signature);
  if (delivery === "send") {
    return {
      title: `Approve and send ${what} now?`,
      body: `The server sends exactly this content from ${ctx.mailbox ?? "your mailbox"}${attached(ctx.attachments)}.${signed}`,
      confirmLabel: "Approve & send",
    };
  }
  return n
    ? {
        title: `Approve ${what} with your ${comments(n)} worked in?`,
        body: `${capitalize(author)} works your ${comments(n)} into the email and creates the mailbox draft. There is no further review round.${signed} You send it from Outlook.`,
        confirmLabel: "Approve & draft",
      }
    : {
        title: `Approve ${what} as a draft?`,
        body: `${capitalize(author)} creates the mailbox draft with exactly this content.${signed} You send it from Outlook.`,
        confirmLabel: "Approve & draft",
      };
}

/** The sentence after a finish; `error` when a requested send did not happen. */
export function finishOutcome(
  review: AdminReviewDoc,
  ctx: FinishContext,
): { text: string; error: boolean } {
  const author = authorOf(ctx);
  if (review.status === "rejected") {
    return { text: "Email rejected – reason sent to the agent; no revision", error: false };
  }
  if (review.status === "changes_requested") {
    return {
      text: ctx.isBriefing
        ? `Comments sent – ${author} works them into the briefing`
        : `Changes requested – ${author} revises and resubmits`,
      error: false,
    };
  }
  if (!ctx.isEmail) {
    return {
      text: ctx.feedbackCount
        ? `Approved – ${author} applies your notes and closes the review`
        : `Approved – ${author} closes the review`,
      error: false,
    };
  }
  if (review.sent_directly) {
    return { text: `Sent from ${review.sent_directly.mailbox}${attached(review.sent_directly.attachments)}`, error: false };
  }
  if (review.direct_send_error) {
    return {
      text: `Approved, but not sent (${review.direct_send_error}) – ${author} creates a draft instead`,
      error: true,
    };
  }
  return {
    text: ctx.feedbackCount
      ? `Approved – ${author} works in your ${comments(ctx.feedbackCount)} and creates the mailbox draft`
      : `Approved – ${author} creates the mailbox draft`,
    error: false,
  };
}
